import { useState, useEffect, useCallback } from 'react'
import { api } from '@/api/client'
import type { EmployerAnmeldung, EmployerAnmeldungDetail } from '@/types'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'
import { Search, ChevronLeft, ChevronRight, X, Building2, User, Mail, MapPin, GraduationCap, Calendar, CheckCircle2, XCircle } from 'lucide-react'
import clsx from 'clsx'

const PAGE_SIZE = 50

export default function AnmeldeportalPage() {
  const [items, setItems] = useState<EmployerAnmeldung[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Suche
  const [searchMode, setSearchMode] = useState(false)
  const [nachname, setNachname] = useState('')
  const [vorname, setVorname] = useState('')
  const [employerName, setEmployerName] = useState('')

  // Detail
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<EmployerAnmeldungDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const loadList = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await api.anmeldungenList({ skip: page * PAGE_SIZE, limit: PAGE_SIZE })
      setItems(data.items)
      setTotal(data.total)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }, [page])

  const doSearch = useCallback(async () => {
    if (!nachname && !vorname && !employerName) {
      setSearchMode(false)
      loadList()
      return
    }
    setLoading(true)
    setError(null)
    setSearchMode(true)
    try {
      const data = await api.anmeldungenSearch({
        nachname: nachname || undefined,
        vorname: vorname || undefined,
        employer_name: employerName || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      })
      setItems(data.items)
      setTotal(data.total)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Fehler bei der Suche')
    } finally {
      setLoading(false)
    }
  }, [nachname, vorname, employerName, page, loadList])

  useEffect(() => {
    if (!searchMode) loadList()
  }, [loadList, searchMode])

  const openDetail = async (id: number) => {
    setSelectedId(id)
    setDetailLoading(true)
    try {
      const d = await api.anmeldungDetail(id)
      setDetail(d)
    } catch {
      setDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  const closeDetail = () => {
    setSelectedId(null)
    setDetail(null)
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(0)
    doSearch()
  }

  const clearSearch = () => {
    setNachname('')
    setVorname('')
    setEmployerName('')
    setSearchMode(false)
    setPage(0)
  }

  const formatDate = (d: string | null) => {
    if (!d) return '–'
    try {
      return new Date(d).toLocaleDateString('de-DE')
    } catch {
      return d
    }
  }

  const formatDateTime = (d: string | null) => {
    if (!d) return '–'
    try {
      return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch {
      return d
    }
  }

  return (
    <div className="px-6 py-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Anmeldeportal</h1>
        <p className="text-sm text-gray-500 mt-1">
          Arbeitgeber-finanzierte Anmeldungen · {total.toLocaleString('de-DE')} Einträge
        </p>
      </div>

      {/* Suchleiste */}
      <form onSubmit={handleSearchSubmit} className="mb-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[0.7rem] font-medium text-gray-500 mb-1">Nachname</label>
          <input
            type="text"
            value={nachname}
            onChange={(e) => setNachname(e.target.value)}
            placeholder="Nachname…"
            className="h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-fom-300 focus:border-fom-400 w-44"
          />
        </div>
        <div>
          <label className="block text-[0.7rem] font-medium text-gray-500 mb-1">Vorname</label>
          <input
            type="text"
            value={vorname}
            onChange={(e) => setVorname(e.target.value)}
            placeholder="Vorname…"
            className="h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-fom-300 focus:border-fom-400 w-44"
          />
        </div>
        <div>
          <label className="block text-[0.7rem] font-medium text-gray-500 mb-1">Arbeitgeber</label>
          <input
            type="text"
            value={employerName}
            onChange={(e) => setEmployerName(e.target.value)}
            placeholder="Firma…"
            className="h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-fom-300 focus:border-fom-400 w-52"
          />
        </div>
        <button
          type="submit"
          className="h-9 px-4 rounded-lg bg-fom-500 text-white text-sm font-medium hover:bg-fom-600 transition-colors flex items-center gap-1.5"
        >
          <Search size={14} />
          Suchen
        </button>
        {searchMode && (
          <button
            type="button"
            onClick={clearSearch}
            className="h-9 px-3 rounded-lg border border-gray-200 text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-1"
          >
            <X size={14} />
            Zurücksetzen
          </button>
        )}
      </form>

      {error && <ErrorAlert message={error} />}

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : (
        <>
          {/* Tabelle */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left font-semibold text-gray-600 px-4 py-3 text-[0.75rem]">ID</th>
                    <th className="text-left font-semibold text-gray-600 px-4 py-3 text-[0.75rem]">Erfasst</th>
                    <th className="text-left font-semibold text-gray-600 px-4 py-3 text-[0.75rem]">Name</th>
                    <th className="text-left font-semibold text-gray-600 px-4 py-3 text-[0.75rem]">E-Mail</th>
                    <th className="text-left font-semibold text-gray-600 px-4 py-3 text-[0.75rem]">Arbeitgeber</th>
                    <th className="text-left font-semibold text-gray-600 px-4 py-3 text-[0.75rem]">Firma (System)</th>
                    <th className="text-left font-semibold text-gray-600 px-4 py-3 text-[0.75rem]">Semester</th>
                    <th className="text-left font-semibold text-gray-600 px-4 py-3 text-[0.75rem]">Ort</th>
                    <th className="text-center font-semibold text-gray-600 px-4 py-3 text-[0.75rem]">Gültig</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => openDetail(item.id)}
                      className={clsx(
                        'cursor-pointer transition-colors hover:bg-fom-50/50',
                        selectedId === item.id && 'bg-fom-50',
                      )}
                    >
                      <td className="px-4 py-2.5 text-gray-400 font-mono text-xs">{item.id}</td>
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{formatDateTime(item.erfasst_am)}</td>
                      <td className="px-4 py-2.5 font-medium text-gray-900">
                        {item.nachname}, {item.vorname}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 max-w-[200px] truncate">{item.email ?? '–'}</td>
                      <td className="px-4 py-2.5 text-gray-600 max-w-[220px] truncate">{item.contact_person_company ?? '–'}</td>
                      <td className="px-4 py-2.5 max-w-[220px] truncate">
                        {item.matched_firma ? (
                          <span className="text-emerald-700 font-medium">{item.matched_firma}</span>
                        ) : (
                          <span className="text-gray-300">–</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{formatDate(item.semester_start)}</td>
                      <td className="px-4 py-2.5 text-gray-600">{item.city ?? '–'}</td>
                      <td className="px-4 py-2.5 text-center">
                        {item.is_valid ? (
                          <CheckCircle2 size={16} className="text-emerald-500 inline" />
                        ) : (
                          <XCircle size={16} className="text-red-400 inline" />
                        )}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-gray-400">
                        Keine Anmeldungen gefunden.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
              <span>
                Seite {page + 1} von {totalPages}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="h-8 w-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="h-8 w-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail-Overlay */}
      {selectedId !== null && (
        <div className="fixed inset-0 z-50 flex items-start justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={closeDetail} />
          <div className="relative w-full max-w-lg h-full bg-white shadow-2xl border-l border-gray-200 overflow-y-auto animate-in slide-in-from-right">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-bold text-gray-900">Anmeldung #{selectedId}</h2>
              <button onClick={closeDetail} className="text-gray-400 hover:text-gray-900 p-1 rounded-lg hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>

            {detailLoading ? (
              <div className="flex justify-center py-20"><Spinner /></div>
            ) : detail ? (
              <div className="px-6 py-5 space-y-6">
                {/* Status */}
                <div className="flex items-center gap-3">
                  <span className={clsx(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold',
                    detail.is_valid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700',
                  )}>
                    {detail.is_valid ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                    {detail.is_valid ? 'Gültig' : 'Ungültig'}
                  </span>
                  {detail.fixed && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                      Fixiert
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">Erfasst: {formatDateTime(detail.erfasst_am)}</span>
                </div>

                {/* Person */}
                <section>
                  <h3 className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    <User size={13} /> Person
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <Field label="Anrede" value={detail.salutation} />
                    <Field label="Titel" value={detail.title} />
                    <Field label="Vorname" value={detail.vorname} />
                    <Field label="Nachname" value={detail.nachname} />
                    <Field label="Geburtsname" value={detail.geburtsname} />
                    <Field label="Geburtstag" value={formatDate(detail.geburtstag)} />
                  </div>
                </section>

                {/* Adresse */}
                <section>
                  <h3 className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    <MapPin size={13} /> Adresse
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <Field label="Straße" value={[detail.street, detail.hausnummer].filter(Boolean).join(' ')} />
                    <Field label="Zusatz" value={detail.adresszusatz} />
                    <Field label="PLZ / Ort" value={[detail.post_code, detail.city].filter(Boolean).join(' ')} />
                    <Field label="Land" value={detail.country} />
                  </div>
                </section>

                {/* Kontakt */}
                <section>
                  <h3 className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    <Mail size={13} /> Kontakt
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <Field label="E-Mail" value={detail.email} />
                    <Field label="Telefon" value={detail.phone} />
                  </div>
                </section>

                {/* Studium */}
                <section>
                  <h3 className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    <GraduationCap size={13} /> Studium
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <Field label="Semester" value={detail.semester} />
                    <Field label="Zeitmodell" value={detail.time_model} />
                    <Field label="Produkt-ID" value={detail.product_id} />
                    <Field label="Produkt-Instanz" value={detail.product_instance} />
                    <Field label="Drittstaat" value={detail.aus_drittstaat ? 'Ja' : 'Nein'} />
                  </div>
                </section>

                {/* Arbeitgeber */}
                <section>
                  <h3 className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    <Building2 size={13} /> Arbeitgeber
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <Field label="Firma (Anmeldung)" value={detail.contact_person_company} className="col-span-2" />
                    {detail.matched_firma && (
                      <div className="col-span-2 bg-emerald-50 rounded-lg px-3 py-2">
                        <dt className="text-[0.7rem] font-medium text-emerald-600">Firma (System)</dt>
                        <dd className="text-emerald-800 font-semibold mt-0.5">{detail.matched_firma}</dd>
                      </div>
                    )}
                    <Field label="Ansprechpartner" value={detail.contact_person} />
                    <Field label="E-Mail AP" value={detail.contact_person_email} />
                  </div>
                </section>

                {/* Zahlung */}
                <section>
                  <h3 className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    <Calendar size={13} /> Zahlung & Zustimmungen
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <Field label="Kontoinhaber" value={detail.account_owner} />
                    <Field label="IBAN" value={detail.iban ? `****${detail.iban.slice(-4)}` : null} />
                    <Field label="SEPA" value={detail.sepa_checkbox != null ? (detail.sepa_checkbox ? 'Ja' : 'Nein') : null} />
                    <Field label="AGB" value={detail.terms_and_conditions != null ? (detail.terms_and_conditions ? 'Ja' : 'Nein') : null} />
                  </div>
                </section>

                {/* UTM */}
                {(detail.utm_source || detail.utm_medium || detail.utm_campaign) && (
                  <section>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                      UTM Tracking
                    </h3>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <Field label="Source" value={detail.utm_source} />
                      <Field label="Medium" value={detail.utm_medium} />
                      <Field label="Campaign" value={detail.utm_campaign} />
                      <Field label="Term" value={detail.utm_term} />
                      <Field label="Content" value={detail.utm_content} />
                    </div>
                  </section>
                )}
              </div>
            ) : (
              <div className="px-6 py-20 text-center text-gray-400">Konnte Anmeldung nicht laden.</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, value, className }: { label: string; value: string | null | undefined; className?: string }) {
  return (
    <div className={className}>
      <dt className="text-[0.7rem] font-medium text-gray-400">{label}</dt>
      <dd className="text-gray-900 mt-0.5">{value || '–'}</dd>
    </div>
  )
}
